import PropTypes from "prop-types";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, MessageCircle, RefreshCw, Smartphone } from "lucide-react";
import { whatsappDisplayMeta } from "@/lib/connectionRows";

export function WhatsAppManageDialog({
  open,
  onOpenChange,
  row,
  canEdit,
  onPairDifferent,
  onDisconnect,
  busy = false,
}) {
  const meta = whatsappDisplayMeta(row);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="whatsapp-manage-dialog" preventOutsideDismiss>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[15px]">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-emerald-100">
              <MessageCircle size={15} className="text-emerald-700" />
            </span>
            <span>Manage WhatsApp</span>
          </DialogTitle>
          <DialogDescription className="text-[12px] text-slate-500">
            Review the linked device or start a fresh pairing for a different phone.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 space-y-2" data-testid="whatsapp-manage-status">
          <div className="flex items-center gap-2 text-emerald-800">
            <CheckCircle2 size={16} />
            <span className="text-[13px] font-semibold">Connected</span>
          </div>
          {meta.phoneNumber ? (
            <div className="text-[13px] text-emerald-900 font-mono">{meta.phoneNumber}</div>
          ) : null}
          {meta.displayName ? (
            <div className="text-[11.5px] text-emerald-800">{meta.displayName}</div>
          ) : null}
          {meta.connectedAt ? (
            <div className="text-[10.5px] text-emerald-700">
              Connected {new Date(meta.connectedAt).toLocaleString()}
            </div>
          ) : null}
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-[11.5px] text-slate-600 leading-relaxed">
          <div className="inline-flex items-center gap-1.5 font-semibold text-slate-800 mb-1">
            <Smartphone size={12} /> Pair a different device
          </div>
          Disconnects the current WhatsApp session on OpenClaw and shows a new QR code.
          You will need to scan and confirm on the new phone.
        </div>

        <div className="flex items-center justify-end gap-2">
          {canEdit ? (
            <>
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-[12px]"
                onClick={() => onDisconnect?.()}
                disabled={busy}
                data-testid="whatsapp-manage-disconnect"
              >
                Disconnect
              </Button>
              <Button
                size="sm"
                className="h-8 text-[12px]"
                onClick={() => onPairDifferent?.()}
                disabled={busy}
                data-testid="whatsapp-manage-repair"
              >
                <RefreshCw size={12} className={busy ? "animate-spin" : ""} />
                Pair different device
              </Button>
            </>
          ) : (
            <Button size="sm" variant="outline" className="h-8 text-[12px]" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

WhatsAppManageDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  row: PropTypes.object,
  canEdit: PropTypes.bool,
  onPairDifferent: PropTypes.func,
  onDisconnect: PropTypes.func,
  busy: PropTypes.bool,
};
